import { useEffect, useState, useRef } from 'react';
import { useOnboarding } from '../../hooks/useOnboarding';
import { OnboardingTooltip } from './OnboardingTooltip';

/**
 * DiscoveryTooltip - Shows when onboarding is not started (first visit only)
 * Auto-opens the user menu dropdown and points to "Guide de démarrage" link
 * Does NOT show after manual reset - user knows where to find it
 */
export function DiscoveryTooltip() {
  const { progress, loading } = useOnboarding();
  const [isVisible, setIsVisible] = useState(false);
  const wasReset = useRef(false);

  // Check if we should show the discovery tooltip (only for truly new users, not after reset)
  const shouldShow = !loading && progress?.status === 'not_started' && !wasReset.current;

  // Listen for reset event - do NOT show tooltip after reset
  useEffect(() => {
    const handleReset = () => {
      wasReset.current = true;
      setIsVisible(false);
    };
    
    window.addEventListener('onboarding:reset', handleReset);
    return () => {
      window.removeEventListener('onboarding:reset', handleReset);
    };
  }, []);

  useEffect(() => {
    if (!shouldShow) {
      setIsVisible(false);
      return;
    }

    // Dispatch event to open the user menu dropdown
    window.dispatchEvent(new CustomEvent('onboarding:open-user-menu'));
    
    // Small delay to let the menu render before showing tooltip
    const timeout = setTimeout(() => {
      setIsVisible(true);
    }, 300);

    return () => clearTimeout(timeout);
  }, [shouldShow]);

  // Listen for when widget opens - hide discovery tooltip
  useEffect(() => {
    const handleWidgetOpen = () => {
      setIsVisible(false);
    };
    
    window.addEventListener('onboarding:open-widget', handleWidgetOpen);
    return () => {
      window.removeEventListener('onboarding:open-widget', handleWidgetOpen);
    };
  }, []);

  if (!shouldShow || !isVisible) {
    return null;
  }

  return (
    <OnboardingTooltip
      targetSelector='[data-onboarding="guide-link"]'
      title="Commencer ICI"
      description="Cliquez sur ce lien pour démarrer le guide de prise en main de Totemium."
      step={1}
      totalSteps={1}
      position="left"
      isVisible={isVisible}
      showNextButton={false}
    />
  );
}

export default DiscoveryTooltip;
